import React from 'react'
import { withErrorBoundary } from '@s4l/utils/hocs/withErrorBoundary'
import { Button, Tag, Typography } from '@s4l/ds'
import { IconArrowRight } from '@s4l/icons/IconArrowRight'
import Image from '../../Atoms/Image'
import type { Room } from '@s4l/stayforlong/booking/Room'
import { IconPicture } from '@s4l/icons/IconPicture'
import { IconPerson } from '@s4l/icons/IconPerson'
import { useS4LContext } from '@s4l/react-context'
import { Facilities } from '../../Atoms/Facilities'
import RoomPreviewPlaceholder from './RoomPreviewPlaceholder'
import { IconFire } from '@s4l/icons/IconFire'

import styles from './styles.module.css'

const MAX_ALLOTMENT_ROOMS = 5
const MAX_FEATURED_FACILITIES = 5

export interface IRoomPreviewProps {
  room: Room
  onClickSeeRoom?: () => void
}

function RoomPreview({ room, onClickSeeRoom }: Readonly<IRoomPreviewProps>) {
  const { i18n } = useS4LContext()
  const alt = 'Room image'

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onClickSeeRoom?.()
    }
  }

  const firstImage = room.firstImage()

  const allotment = room.allotment()

  const allotmentText = room.isLastRoom()
    ? i18n.formatMessage({ id: 'allotment.last-room' })
    : i18n.formatMessage({ id: 'allotment.only-rooms' }, { roomsLeft: allotment })

  const adults = room.adults()
  const children = room.children()

  const adultsText = room.isMoreThanOneAdult()
    ? i18n.formatMessage({ id: 'global.adults' })
    : i18n.formatMessage({ id: 'global.adult' })

  const childrenText = room.isMoreThanOneChild()
    ? i18n.formatMessage({ id: 'global.children' })
    : i18n.formatMessage({ id: 'global.child' })

  const adultsLabel = `${adults} ${adultsText}`
  const childrenLabel = children > 0 ? ` · ${children} ${childrenText}` : null

  const imagePlaceholder = room.imagePlaceholder()

  return (
    <header className={styles.roomHeader} data-testid="room-header">
      {firstImage ? (
        <button
          type="button"
          className={styles.roomImageContainer}
          onClick={onClickSeeRoom}
          onKeyDown={handleKeyDown}
        >
          <div className={styles.roomImage}>
            <Image.Model image={firstImage} alt={alt} preset="roomHeader" loading="eager" />
          </div>
          {room.hasImages() && (
            <div className={styles.roomImagesNumber}>
              <IconPicture size={14} />
              <Typography variant="caption" size="l" bold color="var(--color-base-white)">
                {room.imagesCount()}
              </Typography>
            </div>
          )}
          {allotment <= MAX_ALLOTMENT_ROOMS && (
            <div className={styles.tagContainer}>
              <Tag size="s" bgColor="var(--color-error-500)" icon={<IconFire />} capitalizeFirst>
                {allotmentText}
              </Tag>
            </div>
          )}
        </button>
      ) : (
        <button
          type="button"
          className={styles.noRoomImage}
          onClick={onClickSeeRoom}
          onKeyDown={handleKeyDown}
        >
          <Image.Picture {...imagePlaceholder} alt={alt} loading="eager" />
        </button>
      )}
      <div className={styles.roomHeaderInfoContainer}>
        <Typography variant="body" size="xl" bold as="h3" clamp={2}>
          {room.isMultiRoom() ? `(x${room.roomsNumber()}) ` : ''} {room.nameCapitalized()}
        </Typography>
        <section className={styles.roomHeaderGuestAndFacilitiesContainer}>
          <span className={styles.roomHeaderGuestContainer}>
            <IconPerson size={12} />
            <Typography variant="caption" size="l" bold>
              {adultsLabel} {childrenLabel}
            </Typography>
          </span>
          <Facilities
            facilitiesJSON={room.featuredFacilities(MAX_FEATURED_FACILITIES).toJSON()}
            size="l"
          />
        </section>
        <div>
          <Button variant="text-flat" onClick={onClickSeeRoom} as="button" size="small">
            {i18n.formatMessage({ id: 'global.see-room' })}
            <IconArrowRight size={16} />
          </Button>
        </div>
      </div>
    </header>
  )
}

RoomPreview.displayName = 'RoomPreview'
RoomPreview.Placeholder = withErrorBoundary(RoomPreviewPlaceholder)

export default withErrorBoundary(RoomPreview)
